import type { z } from 'zod';
import { Problem } from '@/shared/contracts';
import { uuidv7 } from '@/shared/ids';

/** A problem+json answer from the server, carried with its trace id so a screen can show it. */
export class ApiProblem extends Error {
  readonly problem: Problem;
  readonly reviewRequired: boolean;
  constructor(problem: Problem, reviewRequired = false) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiProblem';
    this.problem = problem;
    this.reviewRequired = reviewRequired;
  }
  get status(): number {
    return this.problem.status;
  }
}

/** Review access expiry is separate from losing the signed-in academy account. */
export const apiEvents = new EventTarget();
export const SIGNED_OUT = 'ea:signed-out';
export const ACCESS_CHECK = 'ea:access-check';
export const REVIEW_REQUIRED = 'ea:review-required';

function announceSignedOut(): void {
  apiEvents.dispatchEvent(new Event(SIGNED_OUT));
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(SIGNED_OUT));
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type CallOptions = {
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
  keepalive?: boolean;
};

async function readProblem(res: Response, traceId: string): Promise<Problem> {
  const fallback: Problem = {
    type: 'about:blank',
    title: res.statusText || `HTTP ${res.status}`,
    status: res.status,
    trace_id: res.headers.get('X-Trace-Id') ?? traceId,
  };
  try {
    const parsed = Problem.safeParse(await res.json());
    return parsed.success ? parsed.data : fallback;
  } catch {
    return fallback;
  }
}

async function checkedResponse(res: Response, traceId: string): Promise<Response> {
  if (res.status === 401 && res.headers.get('X-Review-Gate') === 'required') {
    apiEvents.dispatchEvent(new Event(REVIEW_REQUIRED));
    throw new ApiProblem(await readProblem(res, traceId), true);
  }
  if (res.status === 401) announceSignedOut();
  if (res.status === 403 || res.status === 404) apiEvents.dispatchEvent(new Event(ACCESS_CHECK));
  if (!res.ok) throw new ApiProblem(await readProblem(res, traceId));
  return res;
}

/**
 * One fetch for the whole client. Every call carries a trace id the server echoes and logs; a
 * problem answer becomes an `ApiProblem`; a success body is validated against the given schema,
 * so a server change that breaks a shape fails here and not inside a component.
 */
async function call<T>(
  method: Method,
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
  opts: CallOptions = {},
): Promise<T> {
  const traceId = uuidv7();
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Trace-Id': traceId };
  const init: RequestInit = { method, headers, credentials: 'same-origin', cache: 'no-store' };
  if (opts.signal) init.signal = opts.signal;
  if (opts.keepalive) init.keepalive = true;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const res = await checkedResponse(await fetch(path, init), traceId);
  if (!schema) return undefined as T;
  if (res.status === 204) return schema.parse(undefined);
  return schema.parse(await res.json());
}

export const api = {
  /** Check failures before offering a file; a problem response must never download as a spreadsheet. */
  download: async (path: string): Promise<{ blob: Blob; filename: string }> => {
    const traceId = uuidv7();
    const res = await checkedResponse(
      await fetch(path, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'X-Trace-Id': traceId,
        },
        credentials: 'same-origin',
        cache: 'no-store',
      }),
      traceId,
    );
    if (
      res.headers.get('Content-Type')?.split(';')[0]?.trim() !==
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    )
      throw new Error('The server did not return an XLSX spreadsheet.');
    return {
      blob: await res.blob(),
      filename:
        /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'academy-export.xlsx',
    };
  },
  get: <T>(path: string, schema: z.ZodType<T>, opts?: CallOptions) =>
    call('GET', path, undefined, schema, opts),
  post: <T>(path: string, body: unknown, schema?: z.ZodType<T>, opts?: CallOptions) =>
    call('POST', path, body, schema, opts),
  put: <T>(path: string, body: unknown, schema?: z.ZodType<T>, opts?: CallOptions) =>
    call('PUT', path, body, schema, opts),
  patch: <T>(path: string, body: unknown, schema?: z.ZodType<T>, opts?: CallOptions) =>
    call('PATCH', path, body, schema, opts),
  del: <T>(path: string, schema?: z.ZodType<T>, opts?: CallOptions & { body?: unknown }) =>
    call('DELETE', path, opts?.body, schema, opts),
  /** A multipart upload (a logo, an audio file). The browser sets the boundary. */
  upload: async <T>(
    path: string,
    form: FormData,
    schema: z.ZodType<T>,
    opts: CallOptions = {},
  ): Promise<T> => {
    const traceId = uuidv7();
    const headers: Record<string, string> = { Accept: 'application/json', 'X-Trace-Id': traceId };
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    const res = await fetch(path, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'same-origin',
      cache: 'no-store',
    });
    await checkedResponse(res, traceId);
    return schema.parse(await res.json());
  },
};

export const isApiProblem = (e: unknown): e is ApiProblem => e instanceof ApiProblem;

/**
 * The client's one retry rule for reads: a dropped connection or a server failure (5xx) is tried
 * once more; a definitive answer (any 4xx, including a sign-out or an expired review gate) never
 * is. `failureCount` is the number of failures before this decision, so this allows one retry.
 */
export const retryOnce = (failureCount: number, error: unknown): boolean =>
  !(isApiProblem(error) && error.status < 500) && failureCount < 1;
